import type { AnalyticsConfig } from "./config"
import { ENV_MEASUREMENT_ID, ENV_PIXEL_ID } from "./constants"
import {
  GTAG_SCRIPT_ELEMENT_ID,
  GTAG_SCRIPT_SRC,
  buildGtagScriptSrc,
  isGtagId,
} from "./vendors/gtag"

/**
 * Story 10.10 — registo de tags: traduz a config de env no conjunto de scripts que REALMENTE
 * carregam. É o que torna "provider configurado" igual a "tag que carrega": antes, um
 * `pixelId` sozinho abria o banner e não gateava nada — pedir consentimento para um tag que
 * não existe é pior do que não pedir.
 */

/** Vendor de um tag resolvido. Hoje só existe o Google Tag (é o que a produção serve). */
export const TAG_KIND_GTAG = "gtag"

export type AnalyticsTagKind = typeof TAG_KIND_GTAG

export type AnalyticsTag = {
  kind: AnalyticsTagKind
  /** `id` do `<Script>` — estável, para o next/script dedupar entre navegações. */
  scriptId: string
  /**
   * Id do vendor, já validado. O bootstrap (`js`/`config`) é empurrado por CÓDIGO com este id,
   * antes do primeiro evento — não por um `<Script>` inline, que correria tarde demais.
   */
  vendorId: string
  src: string
  /**
   * Prefixo pelo qual o script do vendor é reconhecido no DOM. Casar por PREFIXO (e não pelo
   * src exato) porque basta um parâmetro acrescentado ao URL para o casamento exato falhar —
   * e falharia para o lado errado: "não recarregar, deixando o terceiro na página".
   */
  srcPrefix: string
}

// Prefixo dos avisos de configuração no console (precedente: `lib/data/*.ts`).
const CONFIG_WARNING_PREFIX = "[analytics]"

// O aviso sai UMA vez por sessão de runtime: é um sinal de cutover, não um log por render.
let hasWarnedAboutIgnoredConfig = false

/**
 * Existe pelo menos um tag carregável para esta config? Predicado BARATO: responde sem
 * construir tag nenhuma (o duplo gate corre a cada render do banner, do rodapé e a cada
 * evento — construir objetos e strings aí era custo puro).
 */
export function hasResolvableTag(config: AnalyticsConfig): boolean {
  const measurementId = config.measurementId
  return Boolean(measurementId && isGtagId(measurementId))
}

/**
 * Tags carregáveis para a config dada. Vazio ⇒ scaffold dormant (env vazia OU id malformado).
 * `pixelId` NÃO resolve tag nenhum: a produção não tem pixel Meta, logo a var fica declarada e
 * inerte até existir uma story com um id real (ver Design Notes da 10.10).
 *
 * PURA de propósito: é chamada em handlers de clique e a cada evento, e o aviso de configuração
 * (efeito de consola) vive em `warnAboutIgnoredConfig`, chamado UMA vez pelo loader. Enfiá-lo
 * aqui punha um `console.warn` — que uma extensão do browser pode substituir por algo que lança
 * — no caminho de gravar a decisão do cliente.
 */
export function resolveTags(config: AnalyticsConfig): AnalyticsTag[] {
  return buildTags(config)
}

export function findGtagTag(
  tags: readonly AnalyticsTag[]
): AnalyticsTag | undefined {
  return tags.find((tag) => tag.kind === TAG_KIND_GTAG)
}

/**
 * O script de algum destes tags já está no DOM? Pergunta ao documento em vez de inferir do
 * cookie: depois de "Gerenciar cookies" a decisão anterior já não existe, mas o terceiro
 * continua carregado — e é isso que decide se a recusa exige recarregar a página.
 * SSR-safe: sem `document` responde `false`.
 */
export function hasLoadedTagInDocument(tags: readonly AnalyticsTag[]): boolean {
  if (typeof document === "undefined") {
    return false
  }
  return tags.some(
    (tag) => document.querySelector(`script[src^="${tag.srcPrefix}"]`) !== null
  )
}

function buildTags(config: AnalyticsConfig): AnalyticsTag[] {
  const measurementId = config.measurementId
  if (!measurementId || !isGtagId(measurementId)) {
    return []
  }
  return [
    {
      kind: TAG_KIND_GTAG,
      scriptId: GTAG_SCRIPT_ELEMENT_ID,
      vendorId: measurementId,
      src: buildGtagScriptSrc(measurementId),
      srcPrefix: GTAG_SCRIPT_SRC,
    },
  ]
}

/**
 * Aviso do cutover MUDO: um id preenchido com um typo (ou o pixel, que não resolve tag nenhum)
 * deixava tudo dormant sem dizer nada — quem ligou a medição só daria pela falta de dados
 * semanas depois. Não é um erro engolido: é uma configuração ignorada, e o gate continua
 * fail-closed de propósito.
 */
export function warnAboutIgnoredConfig(config: AnalyticsConfig): void {
  if (hasWarnedAboutIgnoredConfig || hasResolvableTag(config)) {
    return
  }
  const measurementId = config.measurementId
  if (measurementId) {
    hasWarnedAboutIgnoredConfig = true
    console.warn(
      `${CONFIG_WARNING_PREFIX} ${ENV_MEASUREMENT_ID} está preenchido com um id fora do formato aceite (G-… ou GT-…): nenhum tag será carregado.`
    )
    return
  }
  if (config.pixelId) {
    hasWarnedAboutIgnoredConfig = true
    console.warn(
      `${CONFIG_WARNING_PREFIX} ${ENV_PIXEL_ID} está preenchido mas não liga tag nenhum (a loja não tem pixel Meta): nenhum tag será carregado.`
    )
  }
}
